import createAuth0Client from '@auth0/auth0-spa-js';
import { navigate } from '@reach/router';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { i18n } from 'i18next';
import JwtDecode from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getUser } from '../../core/api/user';
import { environment } from '../../core/config/environment';
import {
  addAuthInterceptor,
  httpClient,
  removeAuthInterceptor,
} from '../../core/config/httpClient';
import { getQueryParam } from '../../core/utils/string.utils';
import { Auth0Client } from '../../global';
import { AuthAppState, AuthContext, AuthProviderProps, LoginParams } from './AuthContext';
import { User } from './authContext.types';

/**
 * Get list of allowed origins for Auth0 redirects.
 * SECURITY: Only domains in this list can be used for authentication.
 * This must match the "Allowed Callback URLs" in your Auth0 application settings.
 */
const getAllowedOrigins = (): string[] => {
  const allowed = [
    'https://companies.royaltyrange.com',
    'https://compid.royaltyrange.com',
    'https://compid-recovery.royaltyrange.com',
    'https://companies.compid.com',
    'https://recovery.compid.com',
    'https://findb-demo.royaltyrange.com',
    'https://compid-demo.royaltyrange.com',
    // Add additional production domains here
    // 'https://recovery.mydomain.com',
  ];

  // Allow localhost for local development
  if (process.env.NODE_ENV === 'development') {
    allowed.push(
      'http://localhost:3000',
      'https://localhost:3000',
      'http://localhost:3001',
      'http://127.0.0.1:3000',
      'http://127.0.0.1:3001',
    );
  }

  return allowed;
};

/**
 * Get and validate the redirect URI based on current origin.
 * Throws an error if the current origin is not in the allowed list.
 */
const getValidatedRedirectUri = (): string => {
  const origin = window.location.origin;
  const allowed = getAllowedOrigins();

  if (!allowed.includes(origin)) {
    const error = `Security Error: Unauthorized origin "${origin}". Allowed origins: ${allowed.join(
      ', ',
    )}`;
    // eslint-disable-next-line no-console
    console.error(error);

    // In production, you might want to redirect to a safe domain or show an error page
    // For now, we'll throw to prevent authentication on unauthorized domains
    throw new Error(error);
  }

  // Log the redirect URI in development for debugging
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('Auth0 redirect URI:', origin + '/');
  }

  return origin + '/';
};

// Create Auth0 client with validated dynamic redirect URI
const auth0 = createAuth0Client({
  client_id: environment.AUTH0_CLIENT_ID,
  domain: environment.AUTH0_DOMAIN,
  redirect_uri: getValidatedRedirectUri(),
  audience: environment.AUTH0_AUDIENCE,
});

const onRedirectCallback = (appState?: AuthAppState) => {
  navigate(appState && appState.targetUrl ? appState.targetUrl : window.location.pathname);
};

const resolveErrorDescription = (
  i18n: i18n,
  error: string | undefined,
  errorDescription: string | undefined,
) => {
  return error && i18n.exists(`app:authorization:error.${error}`)
    ? i18n.t(`app:authorization:error.${error}`)
    : errorDescription;
};

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User>();
  const [tokenExpiration, setTokenExpiration] = useState<number>();
  const [auth0Client, setAuth0] = useState<Auth0Client>();
  const [canLogin, setCanLogin] = useState<boolean>(true);
  const [isLoading, setLoading] = useState(true);
  const { i18n } = useTranslation();

  useEffect(() => {
    const initAuth0 = async () => {
      try {
        const auth0FromHook = await auth0;
        setAuth0(auth0FromHook);

        // If redirected from successful login
        if (window.location.search.includes('code=')) {
          const { appState } = await auth0FromHook.handleRedirectCallback();

          onRedirectCallback(appState);
        }

        if (window.location.search.includes('error=')) {
          setCanLogin(false);
          setLoading(false);

          const error = getQueryParam('error', window.location.search);
          const errorDescription = getQueryParam('error_description', window.location.search);
          const resolvedErrorDescription = resolveErrorDescription(i18n, error, errorDescription);

          if (resolvedErrorDescription) {
            // eslint-disable-next-line
            console.error(resolvedErrorDescription);
          }

          return;
        }

        const isAuthenticated = await auth0FromHook.isAuthenticated();

        if (isAuthenticated) {
          const accessToken = await auth0FromHook.getTokenSilently();

          addAuthInterceptor({
            accessToken,
            tokenRefresh: () => auth0FromHook.loginWithRedirect(),
          });
          const { exp } = JwtDecode(accessToken);
          setTokenExpiration(exp);

          const userFFDB = await getUser(accessToken);
          const user = await auth0FromHook.getUser();
          user.canAccess = userFFDB.data.canAccess;
          user.fullName = userFFDB.data.fullName;
          user.searchResultLimit = userFFDB.data.searchResultLimit;
          user.canAccessPublicDb = userFFDB.data.canAccessPublicDb;
          user.canAccessPrivateDb = userFFDB.data.canAccessPrivateDb;
          user.isTrialUser = userFFDB.data.isTrialUser;
          user.isAdminUser = userFFDB.data.isAdminUser;
          user.showPreviewFeatures = userFFDB.data.showPreviewFeatures;
          user.accessRoles = userFFDB.data.accessRoles;
          user.pillarsAccess = userFFDB.data.pillarsAccess;
          user.hasCustomReport = userFFDB.data.hasCustomReport;

          setUser(user);
        }
      } catch (e) {
        // eslint-disable-next-line
        console.error('Auth error: ', e);
      }

      setLoading(false);
    };

    initAuth0();
    // eslint-disable-next-line
  }, []);

  const HEARTBEAT_INTERVAL_MS = 60 * 1000;

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (tokenExpiration && Date.now() < tokenExpiration * 1000) {
        httpClient.post('/user/heartbeat');
      }
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenExpiration]);

  const login = async (params: LoginParams = {}) => {
    if (!auth0Client) {
      return;
    }

    await auth0Client.loginWithRedirect(params);
  };

  const logout = async () => {
    if (!auth0Client) {
      return;
    }

    await httpClient.post('/user/logout');
    auth0Client.logout({ returnTo: getValidatedRedirectUri() });
    removeAuthInterceptor();
  };

  const clearSessions = async () => {
    if (!auth0Client) {
      return;
    }

    await httpClient.post('/user/clear-user-sessions');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userId: user?.sub,
        isLoading,
        login,
        logout,
        clearSessions,
        canLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
